import type { ShipmentFlowState } from '../../../core/services/shipment-flow-state.service';
import {
  getEmptyAddressFormValue,
  getEmptyReturnAddressFormValue,
} from '../../ship-from-ship-to/forms/shipment-addresses.form';
import {
  applyRatedSpecialHandlingPieceCounts,
  countSpecialHandlingPiecesByCode,
} from '../../service-step/sections/delivery-service-selection/build-delivery-service-requests';
import {
  buildEstimateBreakdown,
  type EstimateBreakdownView,
} from '../../service-step/sections/delivery-service-selection/delivery-service-matrix';
import type { SelectedDeliveryService } from '../../service-step/sections/delivery-service-selection/delivery-service-selection.model';
import { buildHomeLocatorUrl } from '../../service-step/sections/pickup-drop-off/mappers/build-home-locator-url';
import {
  mapReviewAdditionalServices,
  type ReviewAdditionalServicesView,
} from './map-review-additional-services';
import {
  mapReviewAddress,
  mapReviewReturnAddress,
  type ReviewAddressView,
  type ReviewReturnAddressView,
} from './map-review-address';
import { mapReviewInduction, type ReviewInductionView } from './map-review-induction';
import {
  mapReviewPackaging,
  mapSpecialHandlingRowsFromPieceCounts,
  type ReviewPackagingView,
} from './map-review-packaging';

/** Options for building shared Review / Get Labels summary views. */
export interface BuildShipmentSummaryViewsOptions {
  locale: 'en' | 'fr';
  homeLocatorBaseUrl: string;
  /**
   * When true (Get Labels), packaging / additional services / induction are null
   * when their source wizard data is missing. Review always maps defaults.
   */
  omitEmptyOptionalCards?: boolean;
}

/** Core summary fields shared by Review & Pay and Get Labels. */
export interface ShipmentSummaryViewsBase {
  shipFromView: ReviewAddressView;
  shipToView: ReviewAddressView;
  returnView: ReviewReturnAddressView;
  dropOffLocatorUrl: string;
  selectedService: SelectedDeliveryService | null;
  breakdown: EstimateBreakdownView | null;
}

/** Get Labels: optional cards may be omitted when wizard data is missing. */
export interface ShipmentSummaryViews extends ShipmentSummaryViewsBase {
  packagingView: ReviewPackagingView | null;
  additionalServicesView: ReviewAdditionalServicesView | null;
  inductionView: ReviewInductionView | null;
}

/** Review & Pay: optional cards always have mapped defaults. */
export interface ShipmentSummaryViewsRequiredCards extends ShipmentSummaryViewsBase {
  packagingView: ReviewPackagingView;
  additionalServicesView: ReviewAdditionalServicesView;
  inductionView: ReviewInductionView;
}

/**
 * Builds address, packaging, induction, and estimate display models from wizard session state.
 * Shared by Review & Pay and Get Labels to avoid duplicated mapping logic.
 */
export function buildShipmentSummaryViews(
  flowState: ShipmentFlowState | null,
  options: BuildShipmentSummaryViewsOptions & { omitEmptyOptionalCards: true },
): ShipmentSummaryViews;
export function buildShipmentSummaryViews(
  flowState: ShipmentFlowState | null,
  options: BuildShipmentSummaryViewsOptions & { omitEmptyOptionalCards?: false },
): ShipmentSummaryViewsRequiredCards;
export function buildShipmentSummaryViews(
  flowState: ShipmentFlowState | null,
  options: BuildShipmentSummaryViewsOptions,
): ShipmentSummaryViews | ShipmentSummaryViewsRequiredCards {
  const addresses = flowState?.addresses;
  const shipFromView = mapReviewAddress(addresses?.shipFrom ?? getEmptyAddressFormValue());
  const shipToView = mapReviewAddress(addresses?.shipTo ?? getEmptyAddressFormValue());
  const returnView = addresses
    ? mapReviewReturnAddress({
        useDifferentReturnAddress: addresses.useDifferentReturnAddress,
        returnAddress: addresses.returnAddress,
        shipFrom: addresses.shipFrom,
      })
    : mapReviewReturnAddress({
        useDifferentReturnAddress: false,
        returnAddress: getEmptyReturnAddressFormValue(),
        shipFrom: getEmptyAddressFormValue(),
      });

  const omitEmpty = options.omitEmptyOptionalCards === true;
  const packageValue = flowState?.package;
  const selectedService = flowState?.selectedService ?? null;
  const ratedSpecialHandlingPieceCounts = selectedService?.ratedSpecialHandlingPieceCounts;

  const packagingMapped = mapReviewPackaging(packageValue, {
    destinationResidential: addresses?.shipTo?.residential,
  });
  // Prefer hierarchy-aware rated counts so Packaging matches Estimated Costs (OM-884).
  const packagingWithRatedRows =
    packagingMapped && ratedSpecialHandlingPieceCounts
      ? {
          ...packagingMapped,
          specialHandlingRows: mapSpecialHandlingRowsFromPieceCounts(
            ratedSpecialHandlingPieceCounts,
          ),
        }
      : packagingMapped;
  const packagingView = omitEmpty && !packageValue ? null : packagingWithRatedRows;

  const hasAdditionalServices =
    flowState?.additionalCoverage != null || flowState?.signatureProtection != null;
  const additionalMapped = mapReviewAdditionalServices(
    flowState?.additionalCoverage,
    flowState?.signatureProtection,
  );
  const additionalServicesView = omitEmpty && !hasAdditionalServices ? null : additionalMapped;

  const inductionMapped = mapReviewInduction(flowState?.pickupDropOff, options.locale);
  const inductionView = omitEmpty && !flowState?.pickupDropOff ? null : inductionMapped;

  const postal = addresses?.shipFrom.postalCode ?? '';
  const dropOffLocatorUrl = buildHomeLocatorUrl(options.homeLocatorBaseUrl, postal, options.locale);

  const specialHandlingPieceCounts = packageValue
    ? countSpecialHandlingPiecesByCode(packageValue, {
        destinationResidential: addresses?.shipTo?.residential,
      })
    : undefined;
  const breakdown = selectedService
    ? buildEstimateBreakdown(selectedService.pricing, {
        specialHandlingPieceCounts: applyRatedSpecialHandlingPieceCounts(
          specialHandlingPieceCounts,
          ratedSpecialHandlingPieceCounts,
        ),
        discountedPricing: selectedService.discountedPricing,
        discountRate: selectedService.couponDiscount?.discountRate,
      })
    : null;

  return {
    shipFromView,
    shipToView,
    returnView,
    packagingView,
    additionalServicesView,
    inductionView,
    dropOffLocatorUrl,
    selectedService,
    breakdown,
  };
}

/**
 * Builds summary views for Angular computed signals, including locale revision tracking.
 */
export function computeShipmentSummaryViews(
  flowState: ShipmentFlowState | null,
  translation: { revision: () => unknown; locale: () => string },
  omitEmptyOptionalCards: true,
  homeLocatorBaseUrl: string,
): ShipmentSummaryViews;
export function computeShipmentSummaryViews(
  flowState: ShipmentFlowState | null,
  translation: { revision: () => unknown; locale: () => string },
  omitEmptyOptionalCards: false,
  homeLocatorBaseUrl: string,
): ShipmentSummaryViewsRequiredCards;
export function computeShipmentSummaryViews(
  flowState: ShipmentFlowState | null,
  translation: { revision: () => unknown; locale: () => string },
  omitEmptyOptionalCards: boolean,
  homeLocatorBaseUrl: string,
): ShipmentSummaryViews | ShipmentSummaryViewsRequiredCards {
  translation.revision();
  const locale = translation.locale() === 'fr' ? 'fr' : 'en';
  if (omitEmptyOptionalCards) {
    return buildShipmentSummaryViews(flowState, {
      locale,
      homeLocatorBaseUrl,
      omitEmptyOptionalCards: true,
    });
  }
  return buildShipmentSummaryViews(flowState, {
    locale,
    homeLocatorBaseUrl,
    omitEmptyOptionalCards: false,
  });
}
